import { Form, Input, notification, Typography } from 'antd';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useLazyGetBranchesForSystemQuery } from '@app/api/Branches';
import type { ModelChain } from '@app/api/Chains';
import { useLazyGetChainQuery, useUpdateChainMutation } from '@app/api/Chains';
import { EditPageButtons, Error403Unauthorized } from '@app/components';
import { SetBranchesComponent } from '@app/components/SetBranchesComponent';
import { BRANCHES_LABEL, OFF, ONE_TIME_CODE } from '@app/constants';
import {
  RIGHTS_CHAINS,
  RIGHTS_IS_CREATE,
  RIGHTS_IS_EDIT,
} from '@app/constants/accessRights';
import {
  ERROR,
  ERROR_REQUIRED_FIELD,
  SUCCESS,
  USER_PRINT_ERROR,
} from '@app/constants/alerts';
import { PREFIX } from '@app/constants/appPrefix';
import { navigation } from '@app/data';
import { useAccessRights, useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import type { NNavigation } from '@app/types';
import type { NBranches } from '@app/types/Branches';
import type { NChains } from '@app/types/Chains';
import type { NUtils } from '@app/types/Utils';

import { adaptBranchToRedux } from '@pages/Chains/AddChain/utils/adaptBranchToRedux';
import { getModelChain } from '@pages/Chains/AddChain/utils/getModelChain';

import type { FC } from 'react';

import '@pages/Chains/AddChain/styles/AddChain.desktop.scss';
import '@pages/Chains/AddChain/styles/AddChain.tablet.scss';

export const AddChain: FC = () => {
  const { hasUserRight: hasUserRightToCreate } = useAccessRights(
    RIGHTS_CHAINS,
    RIGHTS_IS_CREATE
  );
  const { hasUserRight: hasUserRightToEdit } = useAccessRights(
    RIGHTS_CHAINS,
    RIGHTS_IS_EDIT
  );
  const [open, setOpen] = useState(false);
  const [selectedBranches, setSelectedBranches] = useState<
    NBranches.ModelBranchItem[]
  >([]);
  const [serverError, setServerError] = useState('');
  const [fetchingServerError, setFetchingServerError] = useState('');
  const [editableChainName, setEditableChainName] = useState('');
  const [allBranches, setAllBranches] = useState<NBranches.ModelBranchItem[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);

  const [getBranchesForSystem] = useLazyGetBranchesForSystemQuery();
  const [getChain] = useLazyGetChainQuery();
  const [updateChain] = useUpdateChainMutation();

  const [form] = Form.useForm();

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const editableChainId = Number(searchParams.get('id'));

  const { userId, database, activeBranchId } = useUserPrint();

  const [, contextHolder] = notification.useNotification();

  const [, setErrorType] = useState<NUtils.ErrorTypes>(undefined);

  const { showNotification } = useContext(NotificationContext);

  const showUserModal = () => {
    setOpen(true);
  };

  const hideUserModal = () => {
    setOpen(false);
  };

  const fetchBranches = useCallback(async () => {
    try {
      const response = await getBranchesForSystem({
        ActiveBranchId: activeBranchId,
      }).unwrap();

      setAllBranches(response);
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      showNotification(message);
    }
  }, [activeBranchId, getBranchesForSystem, showNotification]);

  useEffect(() => {
    fetchBranches();
  }, [userId, database, activeBranchId, fetchBranches]);

  const fetchChainData = useCallback(async () => {
    try {
      const result = await getChain({
        ActiveBranchId: activeBranchId,
        chainId: editableChainId,
      }).unwrap();
      setEditableChainName(result.Name || '');

      const incompleteChainBranches: ModelChain['ChainBranches'] = (
        result.ChainBranches || []
      ).filter(({ IsSelected }) => IsSelected);

      const adaptedIncompleteChainBranches: NChains.ChainBranches[] =
        incompleteChainBranches.map((branch) => adaptBranchToRedux(branch));

      const completeChainBranches = allBranches.filter((branch) =>
        adaptedIncompleteChainBranches.find(
          ({ branchId }) => branchId === branch.Id
        )
      );

      setSelectedBranches(completeChainBranches);
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      setFetchingServerError(message);
    }
  }, [getChain, activeBranchId, editableChainId, allBranches]);

  useEffect(() => {
    if (
      userId &&
      database &&
      activeBranchId &&
      editableChainId &&
      allBranches.length
    ) {
      fetchChainData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, database, activeBranchId, editableChainId, allBranches.length]);

  useEffect(() => {
    form.setFieldsValue({ chainName: editableChainName });
  }, [editableChainName, form]);

  const onFinish = async ({ chainName }: { chainName: string }) => {
    const modelChain = getModelChain({
      Id: editableChainId || 0,
      Name: chainName,
      CountBranches: selectedBranches.length,
      IsDeleted: false,
      ChainBranches: selectedBranches,
    });

    if (userId && database && activeBranchId) {
      const requestParams = {
        ActiveBranchId: activeBranchId,
        data: modelChain,
      };

      try {
        setIsLoading(true);
        await updateChain(requestParams).unwrap();

        navigate(
          `${
            (navigation as NNavigation.SidebarItem[]).find(
              ({ id: _id }) => _id === 'chains'
            )?.path
          }`
        );
        const successMessage = editableChainId
          ? 'Сеть обновлена успешно'
          : 'Сеть создана успешно';

        showNotification(successMessage, SUCCESS);
      } catch (error) {
        setErrorType(ERROR);
        const message = error instanceof Error ? error.message : '';
        setServerError(message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCancel = useCallback(() => {
    navigate('/chains');
  }, [navigate]);

  if (!userId || !database || !activeBranchId) {
    return <div>{USER_PRINT_ERROR}</div>;
  }

  if (!editableChainId && !hasUserRightToCreate) {
    return <Error403Unauthorized />;
  }
  if (editableChainId && !hasUserRightToEdit) {
    return <Error403Unauthorized />;
  }

  return (
    <div className={`${PREFIX}AddChain`}>
      {contextHolder}
      <Form.Provider
        onFormFinish={(name) => {
          if (name === 'selectBranchesForm') {
            setOpen(false);
          }
        }}
      >
        <Form
          name="basicForm"
          style={{ maxWidth: 800 }}
          onFinish={onFinish}
          autoComplete={OFF}
          form={form}
        >
          <div className="container">
            <Typography.Text
              className="label-require"
              strong
              data-testid="name-chain-title"
            >
              Название
            </Typography.Text>

            <Form.Item
              name="chainName"
              rules={[
                {
                  required: true,
                  message: generateSpanWithDataTestId(
                    ERROR_REQUIRED_FIELD,
                    'alert'
                  ),
                },
              ]}
            >
              <Input
                data-testid="name-chain-input"
                autoComplete={ONE_TIME_CODE}
                maxLength={300}
                size="large"
              />
            </Form.Item>
          </div>

          <Typography.Title level={3} className="mg-0" data-testid="branches">
            {BRANCHES_LABEL}
          </Typography.Title>
          <SetBranchesComponent
            open={open}
            onCancel={hideUserModal}
            selectedBranches={selectedBranches}
            setSelectedBranches={setSelectedBranches}
            allBranches={allBranches}
            serverError={fetchingServerError}
            showUserModal={showUserModal}
            newDataFieldValue="selectedBranches"
          />

          <EditPageButtons
            serverError={serverError}
            errorType={ERROR}
            setServerError={setServerError}
            isLoading={isLoading}
            handleCancel={handleCancel}
            isEditPage={false}
          />
        </Form>
      </Form.Provider>
    </div>
  );
};
